/**
 * Analyzes imported addon contents: extracts every identifier and namespace,
 * indexes lang keys, and reports conflicts, which is the core reason we import a
 * `.mcaddon` (check lang files, find conflicting items, identify the namespace
 * used across the project).
 */

import type { Project } from "../model/types";
import { fullId } from "../model/identifiers";
import { localeFromPath, parseLang } from "./lang";
import type { AddonContents } from "./mcaddon";
import { detectGuide, detectGuideNamespace } from "./guideLang";

export type ConflictSeverity = "error" | "warning" | "info";

export interface Conflict {
  severity: ConflictSeverity;
  code: string;
  message: string;
  where?: string[];
}

export interface ItemIdentifier {
  identifier: string;
  namespace: string;
  name: string;
  path: string;
}

export interface RecipeIdentifier {
  identifier: string;
  namespace: string;
  name: string;
  resultItem?: string;
  path: string;
}

export interface LangKeyRecord {
  key: string;
  value: string;
  locale: string;
  path: string;
  line: number;
}

export interface GuideCategorySummary {
  id: string;
  entryCount: number;
}

export interface GuideSummary {
  /** Namespace the guide hierarchy lives under (may differ from item namespace). */
  namespace?: string;
  categories: GuideCategorySummary[];
  totalEntries: number;
}

export interface AddonAnalysis {
  packs: { name: string; type: string; path: string }[];
  items: ItemIdentifier[];
  recipes: RecipeIdentifier[];
  namespaces: { namespace: string; items: number; recipes: number; total: number }[];
  primaryNamespace?: string;
  langKeys: LangKeyRecord[];
  langFiles: { path: string; locale: string; keyCount: number }[];
  guide: GuideSummary;
  conflicts: Conflict[];
}

function splitId(id: string): { namespace: string; name: string } | undefined {
  const idx = id.indexOf(":");
  if (idx === -1) return undefined;
  return { namespace: id.slice(0, idx), name: id.slice(idx + 1) };
}

function extractItems(contents: AddonContents): ItemIdentifier[] {
  const out: ItemIdentifier[] = [];
  for (const { path, json } of contents.itemFiles) {
    const id = (json as any)?.["minecraft:item"]?.description?.identifier;
    if (typeof id !== "string") continue;
    const parts = splitId(id);
    if (!parts) continue;
    out.push({ identifier: id, namespace: parts.namespace, name: parts.name, path });
  }
  return out;
}

function extractRecipes(contents: AddonContents): RecipeIdentifier[] {
  const out: RecipeIdentifier[] = [];
  for (const { path, json } of contents.recipeFiles) {
    const body =
      (json as any)?.["minecraft:recipe_shapeless"] ??
      (json as any)?.["minecraft:recipe_shaped"] ??
      (json as any)?.["minecraft:recipe_furnace"] ??
      (json as any)?.["minecraft:recipe_brewing_mix"] ??
      (json as any)?.["minecraft:recipe_brewing_container"];
    const id = body?.description?.identifier;
    if (typeof id !== "string") continue;
    const parts = splitId(id);
    if (!parts) continue;
    const resultItem = typeof body?.result?.item === "string" ? body.result.item : body?.result?.[0]?.item;
    out.push({ identifier: id, namespace: parts.namespace, name: parts.name, resultItem, path });
  }
  return out;
}

function indexLang(contents: AddonContents): { records: LangKeyRecord[]; files: AddonAnalysis["langFiles"] } {
  const records: LangKeyRecord[] = [];
  const files: AddonAnalysis["langFiles"] = [];
  for (const { path, content } of contents.langFiles) {
    const locale = localeFromPath(path);
    const parsed = parseLang(content);
    files.push({ path, locale, keyCount: parsed.lines.length });
    for (const l of parsed.lines) {
      records.push({ key: l.key, value: l.value, locale, path, line: l.line });
    }
  }
  return { records, files };
}

export function analyzeAddon(contents: AddonContents, currentProject?: Project): AddonAnalysis {
  const items = extractItems(contents);
  const recipes = extractRecipes(contents);
  const { records: langKeys, files: langFiles } = indexLang(contents);

  // Namespace tally.
  const nsMap = new Map<string, { items: number; recipes: number }>();
  const bump = (ns: string, key: "items" | "recipes") => {
    const cur = nsMap.get(ns) ?? { items: 0, recipes: 0 };
    cur[key]++;
    nsMap.set(ns, cur);
  };
  items.forEach((i) => bump(i.namespace, "items"));
  recipes.forEach((r) => bump(r.namespace, "recipes"));
  const namespaces = [...nsMap.entries()]
    .map(([namespace, v]) => ({ namespace, items: v.items, recipes: v.recipes, total: v.items + v.recipes }))
    .filter((n) => n.namespace !== "minecraft")
    .sort((a, b) => b.total - a.total);
  const primaryNamespace = namespaces[0]?.namespace;

  // Detect the category-based guide from lang keys (en_US ordering preferred).
  const refKeys = langKeys.filter((k) => k.locale === "en_US");
  const orderedKeys = (refKeys.length ? refKeys : langKeys).map((k) => k.key);
  const guideNamespace = detectGuideNamespace(orderedKeys) ?? primaryNamespace;
  const detected = guideNamespace ? detectGuide(guideNamespace, orderedKeys) : undefined;
  const guide: GuideSummary = {
    namespace: guideNamespace,
    categories: (detected?.categories ?? []).map((c) => ({ id: c.id, entryCount: c.entries.length })),
    totalEntries: (detected?.categories ?? []).reduce((sum, c) => sum + c.entries.length, 0),
  };

  const conflicts: Conflict[] = [];

  // Report the discovered guide structure.
  if (guide.categories.length > 0) {
    conflicts.push({
      severity: "info",
      code: "guide.detected",
      message: `Found a guidebook under "${guide.namespace}": ${guide.categories.length} categor${guide.categories.length === 1 ? "y" : "ies"}, ${guide.totalEntries} entr${guide.totalEntries === 1 ? "y" : "ies"} (${guide.categories.map((c) => `${c.id}:${c.entryCount}`).join(", ")}).`,
    });
  }

  // 1. Duplicate item identifiers across files (conflicting items).
  dupCheck(
    items.map((i) => ({ id: i.identifier, path: i.path })),
    conflicts,
    "item.duplicate",
    "item identifier",
    "error",
  );

  // 2. Duplicate recipe identifiers.
  dupCheck(
    recipes.map((r) => ({ id: r.identifier, path: r.path })),
    conflicts,
    "recipe.duplicate",
    "recipe identifier",
    "error",
  );

  // 3. Vanilla namespace misuse in custom definitions.
  for (const i of items) {
    if (i.namespace === "minecraft") {
      conflicts.push({ severity: "error", code: "item.vanilla_namespace", message: `Item "${i.identifier}" uses the reserved "minecraft" namespace.`, where: [i.path] });
    }
  }

  // 4. Duplicate lang keys within the same locale (across files too).
  const byLocaleKey = new Map<string, LangKeyRecord[]>();
  for (const rec of langKeys) {
    const k = `${rec.locale} ${rec.key}`;
    const arr = byLocaleKey.get(k) ?? [];
    arr.push(rec);
    byLocaleKey.set(k, arr);
  }
  for (const [, recs] of byLocaleKey) {
    if (recs.length > 1) {
      const first = recs[0];
      conflicts.push({
        severity: "warning",
        code: "lang.duplicate_key",
        message: `Lang key "${first.key}" is defined ${recs.length}× for locale ${first.locale}. The last definition wins in-game.`,
        where: recs.map((r) => `${r.path}:${r.line}`),
      });
    }
  }

  // 5. Item defined but missing a display-name key (item.<id>) in every locale.
  const langKeySet = new Set(langKeys.map((k) => k.key));
  for (const i of items) {
    if (!langKeySet.has(`item.${i.identifier}`)) {
      conflicts.push({
        severity: "warning",
        code: "item.missing_name",
        message: `Item "${i.identifier}" has no "item.${i.identifier}" display-name key in any lang file.`,
        where: [i.path],
      });
    }
  }

  // 6. Multiple project namespaces (helps identify the intended namespace).
  if (namespaces.length > 1) {
    conflicts.push({
      severity: "info",
      code: "namespace.multiple",
      message: `Multiple non-vanilla namespaces detected: ${namespaces.map((n) => `${n.namespace} (${n.total})`).join(", ")}. The primary appears to be "${primaryNamespace}".`,
    });
  }

  // 7. Cross-check against the currently open project (collisions on import/merge).
  if (currentProject) {
    const currentIds = new Set<string>([
      fullId(currentProject.namespace, currentProject.guideItemId),
      ...currentProject.items.map((it) => fullId(currentProject.namespace, it.id)),
    ]);
    for (const i of items) {
      if (currentIds.has(i.identifier)) {
        conflicts.push({
          severity: "error",
          code: "item.collision_with_project",
          message: `Imported item "${i.identifier}" collides with an item already in your open project.`,
          where: [i.path],
        });
      }
    }
  }

  return {
    packs: contents.manifests.map((m) => ({ name: m.headerName, type: m.packType, path: m.path })),
    items,
    recipes,
    namespaces,
    primaryNamespace,
    langKeys,
    langFiles,
    guide,
    conflicts: sortConflicts(conflicts),
  };
}

function dupCheck(
  entries: { id: string; path: string }[],
  out: Conflict[],
  code: string,
  label: string,
  severity: ConflictSeverity,
) {
  const map = new Map<string, string[]>();
  for (const e of entries) {
    const arr = map.get(e.id) ?? [];
    arr.push(e.path);
    map.set(e.id, arr);
  }
  for (const [id, paths] of map) {
    if (paths.length > 1) {
      out.push({ severity, code, message: `Duplicate ${label} "${id}" defined in ${paths.length} files.`, where: paths });
    }
  }
}

const SEVERITY_ORDER: Record<ConflictSeverity, number> = { error: 0, warning: 1, info: 2 };
function sortConflicts(conflicts: Conflict[]): Conflict[] {
  return [...conflicts].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
}
